import React, { useState } from "react";
import { Card, Button, Alert, Spinner, Badge, Form } from "react-bootstrap";
import { useUser } from "../contexts/UserContext";
import "./SubscriptionManager.css";

const SubscriptionManager = () => {
  const {
    user,
    subscriptionStatus,
    manageSubscription,
    createDonationSession,
    createSubscriptionWithDonation,
  } = useUser();

  // TODO: Pull pricing from Stripe when real keys are configured
  const SUBSCRIPTION_PRICE = "$5.00";
  const SUBSCRIPTION_TERM = "year";

  // State for donation option
  const [includeDonation, setIncludeDonation] = useState(false);
  const [donationAmount, setDonationAmount] = useState(10);
  const [customAmount, setCustomAmount] = useState("");
  const [showDonationAmounts, setShowDonationAmounts] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isDonating, setIsDonating] = useState(false);

  // Error state for debugging
  const [errorMessage, setErrorMessage] = useState("");

  const quickAmounts = [5, 10, 25, 50];

  const handleQuickAmount = (amount) => {
    setDonationAmount(amount);
    setCustomAmount(""); // Clear custom input when quick amount is selected
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    setCustomAmount(value);
    if (value && !isNaN(value) && parseFloat(value) > 0) {
      setDonationAmount(parseFloat(value));
    }
  };

  // Debug logging for subscription status
  React.useEffect(() => {
    console.log(
      "[SubscriptionManager] Subscription status data:",
      subscriptionStatus
    );
    if (subscriptionStatus) {
      console.log("[SubscriptionManager] Status:", subscriptionStatus.status);
      console.log(
        "[SubscriptionManager] Subscription object:",
        subscriptionStatus.subscription
      );
      console.log(
        "[SubscriptionManager] Full object:",
        JSON.stringify(subscriptionStatus, null, 2)
      );
    }
  }, [subscriptionStatus]);

  if (!user) {
    return null;
  }

  const handleManageSubscription = async () => {
    try {
      setErrorMessage(""); // Clear any previous errors
      console.log("[SubscriptionManager] Manage subscription clicked");
      const result = await manageSubscription();
      console.log("[SubscriptionManager] Manage subscription result:", result);

      if (result && result.url) {
        console.log(
          "[SubscriptionManager] Opening portal in same tab:",
          result.url
        );
        // For customer portal, direct navigation is fine
        window.location.assign(result.url);
      } else {
        throw new Error("No portal URL received");
      }
    } catch (error) {
      console.error(
        "[SubscriptionManager] Error managing subscription:",
        error
      );
      const errorMsg = `Subscription portal error: ${error.message}`;
      setErrorMessage(errorMsg);
    }
  };

  const handleDonation = async () => {
    if (isDonating) return;

    setIsDonating(true);
    setErrorMessage(""); // Clear any previous errors
    try {
      console.log(
        "[SubscriptionManager] Creating donation session, amount:",
        donationAmount
      );
      const result = await createDonationSession(donationAmount, "one-time");
      console.log("[SubscriptionManager] Donation session result:", result);

      if (result && result.url) {
        console.log("[SubscriptionManager] Redirecting to donation checkout");
        // Direct navigation - most reliable for Stripe checkout
        window.location.assign(result.url);
      } else {
        throw new Error("No donation URL received");
      }
    } catch (error) {
      console.error(
        "[SubscriptionManager] Error creating donation session:",
        error
      );
      const errorMsg = `Donation error: ${error.message}`;
      setErrorMessage(errorMsg);
      setIsDonating(false);
    }
  };

  const handleToggleDonationAmounts = () => {
    setShowDonationAmounts(!showDonationAmounts);
  };

  const handleSubscription = async () => {
    if (isSubscribing) return;

    setIsSubscribing(true);
    setErrorMessage(""); // Clear any previous errors
    try {
      console.log(
        "[SubscriptionManager] Creating subscription session, include donation:",
        includeDonation
      );
      let result;
      if (includeDonation) {
        result = await createSubscriptionWithDonation(
          true,
          donationAmount,
          "one-time"
        );
      } else {
        result = await manageSubscription();
      }

      console.log("[SubscriptionManager] Subscription session result:", result);

      if (result && result.url) {
        console.log(
          "[SubscriptionManager] Redirecting to subscription checkout"
        );
        // Direct navigation - most reliable for Stripe checkout
        window.location.assign(result.url);
      } else {
        throw new Error("No subscription URL received");
      }
    } catch (error) {
      console.error(
        "[SubscriptionManager] Error creating subscription session:",
        error
      );
      const errorMsg = `Subscription error: ${error.message}`;
      setErrorMessage(errorMsg);
      setIsSubscribing(false);
    }
  };

  if (subscriptionStatus === undefined) {
    console.log(
      "[SubscriptionManager] Subscription status undefined, showing spinner"
    );
    return (
      <div className="text-center p-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">
            Loading subscription status...
          </span>
        </Spinner>
      </div>
    );
  }

  console.log(
    "[SubscriptionManager] Rendering with subscription status:",
    subscriptionStatus?.status === "active" ? "ACTIVE" : "INACTIVE"
  );

  return (
    <Card className="subscription-manager">
      <Card.Body>
        <Card.Title>Your Subscription</Card.Title>

        {/* Error Alert for Debugging */}
        {errorMessage && (
          <Alert variant="danger" className="mb-3">
            <Alert.Heading>Error</Alert.Heading>
            <p className="mb-2">{errorMessage}</p>
            <small className="text-muted">
              User Agent: {navigator.userAgent.substring(0, 100)}...
            </small>
            <div className="mt-2">
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => setErrorMessage("")}
              >
                Dismiss
              </Button>
            </div>
          </Alert>
        )}

        {subscriptionStatus?.status === "active" ? (
          <>
            <Alert variant="success">
              <Alert.Heading>Active Subscription</Alert.Heading>
              <p>You have access to all premium games and features!</p>
              {subscriptionStatus.renewalDate && (
                <p className="mb-0">
                  Next renewal:{" "}
                  {new Date(
                    subscriptionStatus.renewalDate
                  ).toLocaleDateString()}
                </p>
              )}
              {!subscriptionStatus.renewalDate && (
                <p className="mb-0 text-muted">
                  <small>Renewal date not available</small>
                </p>
              )}
            </Alert>

            {/* Donation Section for Active Subscribers */}
            {showDonationAmounts && (
              <div className="sm-donation-option mb-3">
                <div className="donation-amount-selection">
                  <div className="mb-2">
                    <small className="text-muted">
                      Choose your donation amount:
                    </small>
                  </div>

                  {/* Quick Amount Buttons */}
                  <div className="quick-amounts mb-3">
                    {quickAmounts.map((amount) => (
                      <Button
                        key={amount}
                        variant={
                          donationAmount === amount && !customAmount
                            ? "primary"
                            : "outline-primary"
                        }
                        size="sm"
                        className="me-2 mb-2"
                        onClick={() => handleQuickAmount(amount)}
                      >
                        ${amount}
                      </Button>
                    ))}
                  </div>

                  {/* Custom Amount Input */}
                  <div className="custom-amount">
                    <Form.Group>
                      <Form.Label className="small text-muted">
                        Or enter custom amount:
                      </Form.Label>
                      <div className="input-group">
                        <span className="input-group-text donation-amount-prefix">
                          $
                        </span>
                        <Form.Control
                          type="number"
                          placeholder="0.00"
                          min="1"
                          step="0.01"
                          value={customAmount}
                          onChange={handleCustomAmountChange}
                          size="sm"
                          className="donation-amount-input"
                        />
                      </div>
                    </Form.Group>
                  </div>

                  <div className="mt-2">
                    <small className="text-success">
                      Thank you! Your <strong>${donationAmount}</strong>{" "}
                      donation helps us continue to improve.
                    </small>
                  </div>
                </div>
              </div>
            )}

            <div className="subscription-actions">
              <Button
                variant="outline-primary"
                onClick={handleManageSubscription}
                className="me-2"
              >
                Manage Subscription
              </Button>
              {!showDonationAmounts ? (
                <Button
                  variant="success"
                  onClick={handleToggleDonationAmounts}
                  className="donation-btn"
                >
                  ❤️ Support Us
                </Button>
              ) : (
                <div className="d-flex gap-2">
                  <Button
                    variant="success"
                    onClick={handleDonation}
                    className="donation-btn"
                    disabled={isDonating}
                  >
                    {isDonating ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Processing...
                      </>
                    ) : (
                      `Donate $${donationAmount}`
                    )}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={handleToggleDonationAmounts}
                    size="sm"
                    disabled={isDonating}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <Alert variant="info">
              <Alert.Heading>Unlock All Games</Alert.Heading>
              <div className="pricing-info mb-3">
                <Badge bg="primary" className="pricing-badge">
                  {SUBSCRIPTION_PRICE}/{SUBSCRIPTION_TERM}
                </Badge>
              </div>
              <p>
                Subscribe to get access to our full collection of educational
                games and premium features!
              </p>
              <ul className="subscription-benefits">
                <li>Access to all premium games</li>
                <li>Unlimited user profiles</li>
                <li>New games added regularly</li>
                <li>Ad-free experience</li>
                <li>Priority support</li>
              </ul>
              <p className="subscription-terms">
                <small>Billed annually. Cancel anytime. No hidden fees.</small>
              </p>

              {/* Donation Option */}
              <div className="sm-donation-option mb-3">
                <div className="sm-donation-checkbox">
                  <input
                    type="checkbox"
                    id="include-donation"
                    checked={includeDonation}
                    onChange={(e) => setIncludeDonation(e.target.checked)}
                    className="sm-checkbox-input"
                  />
                  <label
                    htmlFor="include-donation"
                    className="sm-checkbox-label"
                  >
                    ❤️ Add donation
                  </label>
                </div>

                {!includeDonation && (
                  <small className="text-muted d-block mt-1">
                    Your donation helps us continue to be a launchpad for
                    creative play.
                  </small>
                )}

                {includeDonation && (
                  <div className="donation-amount-selection mt-3">
                    <div className="mb-2">
                      <small className="text-muted">
                        Choose your donation amount:
                      </small>
                    </div>

                    {/* Quick Amount Buttons */}
                    <div className="quick-amounts mb-3">
                      {quickAmounts.map((amount) => (
                        <Button
                          key={amount}
                          variant={
                            donationAmount === amount && !customAmount
                              ? "primary"
                              : "outline-primary"
                          }
                          size="sm"
                          className="me-2 mb-2"
                          onClick={() => handleQuickAmount(amount)}
                        >
                          ${amount}
                        </Button>
                      ))}
                    </div>

                    {/* Custom Amount Input */}
                    <div className="custom-amount">
                      <Form.Group>
                        <Form.Label className="small text-muted">
                          Or enter custom amount:
                        </Form.Label>
                        <div className="input-group">
                          <span className="input-group-text donation-amount-prefix">
                            $
                          </span>
                          <Form.Control
                            type="number"
                            placeholder="0.00"
                            min="1"
                            step="0.01"
                            value={customAmount}
                            onChange={handleCustomAmountChange}
                            size="sm"
                            className="donation-amount-input"
                          />
                        </div>
                      </Form.Group>
                    </div>

                    <div className="mt-2">
                      <small className="text-success">
                        Thank you! Your <strong>${donationAmount}</strong>{" "}
                        donation helps us continue to improve.
                      </small>
                    </div>
                  </div>
                )}
              </div>
            </Alert>
            <Button
              variant="primary"
              onClick={handleSubscription}
              className="mt-3"
              disabled={isSubscribing}
            >
              {isSubscribing ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Creating checkout session...
                </>
              ) : includeDonation ? (
                `Subscribe (${SUBSCRIPTION_PRICE}/${SUBSCRIPTION_TERM}) + $${donationAmount} Donation`
              ) : (
                `Subscribe Now - ${SUBSCRIPTION_PRICE}/${SUBSCRIPTION_TERM}`
              )}
            </Button>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default SubscriptionManager;
